import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Building2, User, RefreshCw, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

function flatten(node, depth, rows) {
  rows.push({ ...node, depth });
  (node.owners || []).forEach((o) => flatten(o, depth + 1, rows));
  return rows;
}

export default function OwnershipTree({ customerId }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/kyc/ownership-tree`);
      setTree(data);
      if (!(data.owners || []).length) toast.info("Fant ingen registrerte aksjonærer i Proff");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const rows = tree ? flatten(tree, 0, []) : [];
  return (
    <div className="space-y-2 mt-6" data-testid="ownership-tree">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-medium">Eierstruktur (hvem kontrollerer selskapet)</h4>
        <Button size="sm" variant="secondary" data-testid="fetch-ownership-tree-btn" onClick={load} disabled={loading} className="text-xs h-8">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> {loading ? "Henter…" : (tree ? "Oppdater" : "Vis eierkjede (Proff)")}
        </Button>
      </div>
      {tree && (
        <div className="rounded-lg border border-border bg-card/40 p-3" data-testid="ownership-tree-result">
          {rows.map((n, i) => {
            const isCompany = n.is_company;
            const Icon = isCompany ? Building2 : User;
            const controls = !isCompany && n.effective_share != null && n.effective_share >= 25;
            return (
              <div key={i} className="flex items-center gap-2 py-1 flex-wrap" style={{ paddingLeft: n.depth * 20 }} data-testid="ownership-node">
                {n.depth > 0 && <CornerDownRight className="w-3 h-3 text-muted-foreground/40 shrink-0" />}
                <Icon className={`w-3.5 h-3.5 shrink-0 ${isCompany ? "text-blue-400" : "text-emerald-400"}`} />
                <span className="text-sm">{n.name}</span>
                {n.org_number && isCompany && n.depth > 0 && (
                  <span className="text-[10px] text-muted-foreground/50">{n.org_number}</span>
                )}
                {n.share != null && <span className="text-xs text-muted-foreground">{n.share}%</span>}
                {n.depth > 1 && n.effective_share != null && (
                  <span className="text-xs text-muted-foreground/60">(≈{n.effective_share}% samlet)</span>
                )}
                {controls && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    Reell rettighetshaver ≥25%
                  </span>
                )}
                {n.unresolved && <span className="text-[10px] text-amber-400">ikke oppløst (utenlandsk/ukjent)</span>}
              </div>
            );
          })}
          {tree.truncated && <p className="text-xs text-amber-400 mt-2">Treet er forkortet (mange eiere eller mange nivåer).</p>}
          <p className="text-xs text-muted-foreground/70 mt-2 border-t border-border pt-2">Kilde: Proff. «%» = direkte eierandel i selskapet over. «samlet» = beregnet gjennomgående eierandel fra toppen. Grønn = fysisk person med ≥25 % samlet kontroll.</p>
        </div>
      )}
    </div>
  );
}
